import { resolvePath, joinPath } from '@shopify/cli-kit/node/path';
import Command from '@shopify/cli-kit/node/base-command';
import { renderTasks, renderSuccess } from '@shopify/cli-kit/node/ui';
import { removeFile, moveFile, readFile, writeFile, fileExists } from '@shopify/cli-kit/node/fs';
import { readAndParsePackageJson, installNodeModules, getPackageManager } from '@shopify/cli-kit/node/node-package-manager';
import { commonFlags, flagsToCamelObject } from '../../../lib/flags.js';
import { getRawRemixConfig } from '../../../lib/remix-config.js';
import { mergePackageJson, replaceFileContent } from '../../../lib/file.js';
import { importLangAstGrep } from '../../../lib/ast.js';
import { getAssetsDir } from '../../../lib/build.js';
import { getCodeFormatOptions, formatCode } from '../../../lib/format-code.js';
import { hasViteConfig } from '../../../lib/vite-config.js';
import { AbortError } from '@shopify/cli-kit/node/error';
import { outputNewline } from '@shopify/cli-kit/node/output';

class SetupVite extends Command {
  static description = "EXPERIMENTAL: Upgrades the project to use Vite.";
  static flags = {
    ...commonFlags.path
  };
  async run() {
    const { flags } = await this.parse(SetupVite);
    const directory = flags.path ? resolvePath(flags.path) : process.cwd();
    await runSetupVite({
      ...flagsToCamelObject(flags),
      directory
    });
  }
}
const tailwindPostCSSConfig = `export default {
  plugins: {
    tailwindcss: {},
    autoprefixer: {},
  },
};
`;
async function runSetupVite({ directory }) {
  outputNewline();
  if (await hasViteConfig(directory)) {
    throw new AbortError("This project already has a Vite config file.");
  }
  const [rawRemixConfig, pkgJson, formatOptions] = await Promise.all([
    getRawRemixConfig(directory),
    readAndParsePackageJson(joinPath(directory, "package.json")),
    getCodeFormatOptions(directory)
  ]);
  const serverEntry = rawRemixConfig.server || "server.js";
  const isTS = serverEntry.endsWith(".ts");
  const fileExt = isTS ? "tsx" : "jsx";
  const viteAssets = await getAssetsDir("vite");
  const usesTailwind = !!rawRemixConfig.tailwind;
  const postCssConfigPath = resolvePath(directory, "postcss.config.js");
  const handlePartialIssue = () => {
  };
  const backgroundWorkPromise = Promise.all([
    removeFile(resolvePath(directory, "remix.config.js")).catch(
      handlePartialIssue
    ),
    moveFile(
      resolvePath(directory, "remix.env.d.ts"),
      resolvePath(directory, "env.d.ts")
    ).then(
      () => replaceFileContent(
        resolvePath(directory, "env.d.ts"),
        false,
        (content) => content.replace('types="@remix-run/dev"', 'types="vite/client"')
      )
    ).catch(handlePartialIssue),
    moveFile(
      resolvePath(directory, ".eslintrc.js"),
      resolvePath(directory, ".eslintrc.cjs")
    ).catch(handlePartialIssue),
    // Adjust PostCSS configuration:
    readFile(resolvePath(directory, "postcss.config.js")).then(async (postCssContent) => {
      const hasTailwindPlugin = postCssContent.includes("tailwindcss");
      const isCJS = postCssContent.includes("module.exports") || postCssContent.includes("exports.") || postCssContent.includes("require(");
      const postCSSFilename = isCJS ? postCssConfigPath.replace(".js", ".cjs") : postCssConfigPath;
      if (!hasTailwindPlugin && usesTailwind) {
        postCssContent = await formatCode(
          postCssContent.replace(
            /(plugins:\s+{)/,
            "$1\n    tailwindcss: {},"
          ),
          formatOptions,
          postCSSFilename
        );
      }
      return writeFile(postCSSFilename, postCssContent);
    }).catch(async () => {
      if (usesTailwind && !await fileExists(postCssConfigPath) && !await fileExists(postCssConfigPath.replace(".js", ".cjs"))) {
        return writeFile(postCssConfigPath, tailwindPostCSSConfig);
      }
    }).catch(handlePartialIssue),
    // Adjust dependencies:
    mergePackageJson(viteAssets, directory, {
      onResult(pkgJson2) {
        if (pkgJson2.dependencies) {
          delete pkgJson2.dependencies["@remix-run/css-bundle"];
        }
        if (pkgJson2.devDependencies) {
          if (pkgJson2.devDependencies["@vanilla-extract/css"]) {
            pkgJson2.devDependencies["@vanilla-extract/vite-plugin"] = "^4.0.0";
            pkgJson2.devDependencies = Object.keys(pkgJson2.devDependencies).sort().reduce(
              (acc, key) => {
                acc[key] = pkgJson2.devDependencies?.[key];
                return acc;
              },
              {}
            );
          }
        }
        return pkgJson2;
      }
    }),
    // Build `vite.config.js` for the project:
    readFile(resolvePath(viteAssets, "vite.config.js")).then(
      async (viteConfigContent) => {
        const hasVanillaExtract = !!pkgJson.devDependencies?.["@vanilla-extract/css"];
        if (hasVanillaExtract) {
          viteConfigContent = viteConfigContent.replace(
            /\n\n/g,
            `
import {vanillaExtractPlugin} from '@vanilla-extract/vite-plugin';

`
          ).replace(/^(\s+)\],/m, "$1  vanillaExtractPlugin(),\n$1],");
        }
        const { future, appDirectory, ignoredRouteFiles, routes } = rawRemixConfig;
        for (const flag of [
          "v3_fetcherPersist",
          "v3_throwAbortReason",
          "v3_relativeSplatPath"
        ]) {
          if (!future?.[flag]) {
            viteConfigContent = viteConfigContent.replace(
              `${flag}: true`,
              `${flag}: false`
            );
          }
        }
        if (appDirectory && appDirectory !== "app") {
          viteConfigContent = viteConfigContent.replace(
            /^(\s+)(future:)/m,
            `$1appDirectory: '${appDirectory}',
$1$2`
          );
        }
        if (ignoredRouteFiles) {
          viteConfigContent = viteConfigContent.replace(
            /^(\s+)(future:)/m,
            `$1ignoredRouteFiles: ${JSON.stringify(ignoredRouteFiles)},
$1$2`
          );
        }
        if (routes) {
          viteConfigContent = viteConfigContent.replace(
            /^(\s+)(future:)/m,
            `$1routes: ${routes.toString()},
$1$2`
          );
        }
        const viteConfigPath = resolvePath(
          directory,
          "vite.config." + fileExt.slice(0, 2)
        );
        viteConfigContent = await formatCode(
          viteConfigContent,
          formatOptions,
          viteConfigPath
        );
        return writeFile(viteConfigPath, viteConfigContent);
      }
    ),
    // Adjust server entry:
    replaceFileContent(
      resolvePath(directory, serverEntry),
      false,
      (content) => (isTS ? "// @ts-ignore\n" : "") + content.replace(
        "@remix-run/dev/server-build",
        "virtual:remix/server-build"
      )
    ),
    // Adjust CSS imports in the project routes:
    importLangAstGrep(fileExt).then(async (astGrep) => {
      const appDirectory = resolvePath(
        directory,
        rawRemixConfig.appDirectory ?? "app"
      );
      const rootFilepath = joinPath(appDirectory, "root." + fileExt);
      await new Promise(async (resolve, reject) => {
        const fileNumber = await astGrep.findInFiles(
          {
            paths: [rootFilepath, joinPath(appDirectory, "routes")],
            matcher: {
              rule: {
                kind: "string_fragment",
                regex: "\\.css$",
                inside: {
                  kind: "import_statement",
                  stopBy: "end"
                }
              }
            }
          },
          async (err, nodes) => {
            if (err) reject(err);
            const nodeMap = {};
            nodes.forEach((node) => {
              if (node.text().endsWith(".module.css")) {
                return;
              }
              const filename = node.getRoot().filename();
              const content = node.getRoot().root().text();
              const position = node.range().end.index;
              const tmp = nodeMap[filename] || { content, positions: [] };
              tmp.positions.push(position);
              nodeMap[filename] = tmp;
            });
            await Promise.all(
              Object.entries(nodeMap).flatMap(
                ([filename, { content, positions }]) => {
                  positions.reverse();
                  for (const position of positions) {
                    content = content.slice(0, position) + "?url" + content.slice(position);
                  }
                  return writeFile(filename, content);
                }
              )
            );
            resolve(null);
          }
        );
        if (fileNumber === 0) resolve(null);
      });
      await replaceFileContent(rootFilepath, formatOptions, (content) => {
        const root = astGrep.parse(content).root();
        const liveReloadRegex = "LiveReload";
        const hasLiveReloadRule = {
          kind: "identifier",
          regex: liveReloadRegex
        };
        const liveReloadImport = root.find({
          rule: {
            kind: "import_specifier",
            regex: liveReloadRegex,
            inside: {
              kind: "import_statement",
              stopBy: "end"
            }
          }
        });
        const liveReloadElements = root.findAll({
          rule: {
            any: [
              {
                kind: "jsx_self_closing_element",
                has: hasLiveReloadRule
              },
              {
                kind: "jsx_element",
                has: {
                  kind: "jsx_opening_element",
                  has: hasLiveReloadRule
                }
              }
            ]
          }
        });
        for (const node of [
          // From bottom to top
          ...liveReloadElements.reverse(),
          liveReloadImport
        ]) {
          if (!node) continue;
          const { start, end } = node.range();
          content = content.slice(0, start.index) + content.slice(end.index);
        }
        return content.replace(/,\s*,/g, ",").replace(
          /import\s+{\s+cssBundleHref\s+}\s+from\s+['"]@remix-run\/css-bundle['"];?\n/,
          ""
        ).replace(/\.\.\.\(\s*cssBundleHref[^)]+\),?/, "");
      });
    })
  ]);
  await renderTasks([
    {
      title: "Updating files",
      task: async () => {
        await backgroundWorkPromise;
      }
    },
    {
      title: "Installing new dependencies",
      task: async () => {
        await installNodeModules({
          directory,
          packageManager: await getPackageManager(directory),
          args: []
        });
      }
    }
  ]);
  renderSuccess({
    headline: `Your Vite project is ready!`,
    body: `We've modified your project to use Vite.
Please use Git to review the changes.`,
    nextSteps: [
      rawRemixConfig.mdx ? "Setup MDX support in Vite: https://remix.run/docs/en/main/future/vite#add-mdx-plugin" : "",
      `See more information about Vite in Remix at https://remix.run/docs/en/main/future/vite`
    ].filter(Boolean)
  });
}

export { SetupVite as default, runSetupVite };
